import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { message } from '@client/components/_ui/feedback/message';
import { AttestationRecipientList } from '@client/components/attestations/send/AttestationRecipientList';
import { AttestationRecipientPicker } from '@client/components/attestations/send/AttestationRecipientPicker';
import { AttestationSendFields } from '@client/components/attestations/send/AttestationSendFields';
import { useAttestationRecipients } from '@client/components/attestations/send/useAttestationRecipients';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgReportTimezone } from '@client/hooks/business/org/useOrgReportTimezone';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Checkbox, Form, Tag } from 'antd';
import { DateTime } from 'luxon';
import { useId, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

type SendInput = RouterInputs['userAttestation']['sendUserAttestations'];

/**
 * Optional prefill — attestation reporting's coverage check opens this overlay
 * with the form and the people it found. Without them the overlay starts empty,
 * as it always has; the sender still confirms everything before sending.
 */
const SendSearchSchema = z.object({
    formSchemaId: z.string().regex(/^\d+$/).optional().catch(undefined),
    userIds: z.array(z.string().regex(/^\d+$/)).max(500).optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/attestations/user-attestations/send')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Send user attestation',
        },
        permissions: ['user_attestation:create'],
    },
    validateSearch: zodValidator(SendSearchSchema),
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <SendUserAttestation />
        </PageController>
    );
}

function SendUserAttestation() {
    const formId = useId();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const [form] = Form.useForm<SendInput>();
    const orgTimezone = useOrgReportTimezone();
    const formDirty = useFormDirty();
    const onCloseRef = useRef<(() => void) | null>(null);
    const prefill = Route.useSearch();

    const [confirmChecked, setConfirmChecked] = useState(false);
    // Any change to who receives the attestation invalidates the confirmation. The picker is state
    // outside the Form, so it also marks the draft dirty itself.
    const recipients = useAttestationRecipients({
        initial: prefill.userIds?.length
            ? {
                  mode: 'specific',
                  userIds: prefill.userIds.map((id) => BigInt(id)),
                  handlingPartyIds: [],
                  excludedUserIds: [],
              }
            : undefined,
        onSelectionChange: () => {
            setConfirmChecked(false);
            formDirty.markDirty();
        },
    });
    const { recipientMode, selectedUserIds, includedUsers, excludedUsers, allBaseUsers, toggleExclusion } = recipients;
    const recipientCount = recipients.recipientCount;

    const selectedFormId = Form.useWatch('formSchemaId', form);

    const { data: formsData } = trpc.userAttestation.getActiveFormsForAttestation.useQuery({});

    const selectedForm = useMemo(() => {
        return formsData?.forms.find((f) => f.id === selectedFormId);
    }, [formsData?.forms, selectedFormId]);

    const { mutate: sendAttestations, isPending } = trpc.userAttestation.sendUserAttestations.useMutation({
        onSuccess: (result) => {
            message.success(`Successfully sent ${result.created} attestation(s)`);
            void trpcUtils.userAttestation.listUserAttestations.invalidate();
            onCloseRef.current?.();
        },
        onError: (error) => {
            message.error(error.message || 'Failed to send attestations');
        },
    });

    const handleSubmit = (values: Omit<SendInput, 'recipients' | 'confirmRecipientCount'>) => {
        const userIds = includedUsers.map((u) => u.userId);

        sendAttestations({
            ...values,
            recipients: {
                userIds,
                sendToAll: false,
                handlingPartyIds: [],
            },
            confirmRecipientCount: userIds.length,
        });
    };

    return (
        <OverlayPage
            isDirty={formDirty.isDirty}
            title="Send user attestation"
            size="large"
            afterClose={() => navigate({ moveInMatches: -1 })}
            footer={({ requestClose }) => (
                <DrawerFooter
                    onCancel={requestClose}
                    formId={formId}
                    okText="Send attestation"
                    okLoading={isPending}
                    okDisabled={recipientCount === 0 || !confirmChecked || !selectedFormId}
                    okDisabledReason="Choose a form and at least one recipient, then confirm"
                />
            )}
        >
            {({ onClose }) => (
                <Form
                    id={formId}
                    form={form}
                    {...formDirty.formProps}
                    layout="vertical"
                    onFinish={(values) => {
                        onCloseRef.current = onClose;
                        handleSubmit(values);
                    }}
                    initialValues={{
                        dueInDays: 7,
                        ...(prefill.formSchemaId ? { formSchemaId: BigInt(prefill.formSchemaId) } : {}),
                    }}
                >
                    <AttestationSendFields
                        forms={formsData?.forms}
                        dueBaseDate={orgTimezone ? DateTime.now().setZone(orgTimezone) : undefined}
                        recipientPicker={<AttestationRecipientPicker recipients={recipients} />}
                    />

                    {(recipientMode !== 'specific' || selectedUserIds.length > 0) && (
                        <AttestationRecipientList
                            recipientMode={recipientMode}
                            includedUsers={includedUsers}
                            excludedUsers={excludedUsers}
                            allBaseUsers={allBaseUsers}
                            onToggleExclusion={toggleExclusion}
                            title={
                                <>
                                    Send to {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
                                </>
                            }
                            footer={
                                <Checkbox
                                    checked={confirmChecked}
                                    onChange={(e) => setConfirmChecked(e.target.checked)}
                                >
                                    I confirm I want to send{' '}
                                    {selectedForm ? (
                                        <>
                                            <strong>{selectedForm.formTitle}</strong>{' '}
                                            <Tag className="mx-1">v{selectedForm.version}</Tag>
                                        </>
                                    ) : (
                                        'this form'
                                    )}{' '}
                                    to{' '}
                                    <strong>
                                        {recipientCount} user{recipientCount !== 1 ? 's' : ''}
                                    </strong>
                                </Checkbox>
                            }
                        />
                    )}
                </Form>
            )}
        </OverlayPage>
    );
}
